import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, ChevronDown, Trash2, Eye } from 'lucide-react';

const AdminProviderDocumentsPage = () => {
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Request Document Update Modal
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestFormData, setRequestFormData] = useState({
    documentType: 'Tax Identification Certificate',
    message: '',
    deadline: ''
  });
  const [selectedDocumentForRequest, setSelectedDocumentForRequest] = useState(null);
  
  // Approve Document Modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalFormData, setApprovalFormData] = useState({
    expiryDate: '12/31/2025',
    verificationLevel: 'Standard Review',
    internalNotes: '',
    entityMatch: false,
    validStamp: false,
    externalApiSync: false
  });
  const [selectedDocumentForApproval, setSelectedDocumentForApproval] = useState(null);
  
  // Reject Document Modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectFormData, setRejectFormData] = useState({
    primaryReason: 'Burry or Unreadable Image',
    detailedExplanation: '',
    notifyEmail: true,
    restrictAccess: false
  });
  const [selectedDocumentForReject, setSelectedDocumentForReject] = useState(null);
  
  // Request Resubmission Modal
  const [showResubmissionModal, setShowResubmissionModal] = useState(false);
  const [resubmissionFormData, setResubmissionFormData] = useState({
    issueDescription: '',
    deadline: '',
    priority: 'Standard',
    restrictPortalAccess: false
  });
  const [selectedDocumentForResubmission, setSelectedDocumentForResubmission] = useState(null);
  const [fetchedData, setFetchedData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request('/admin/providers/documents', 'GET');
        if (response.success && response.data) {
          setFetchedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const documents = fetchedData.map((d: any) => {
    const providerName = d.provider_name || d.provider || '—';
    const initials = providerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '??';
    const rawStatus = (d.status || 'UNDER REVIEW').toUpperCase();
    return {
      id: d.id,
      name: d.document_name || d.doc_name || d.name || '—',
      category: d.category || d.document_type || '—',
      provider: providerName,
      avatar: initials,
      uploaded: d.uploaded_at || d.created_at
        ? new Date(d.uploaded_at || d.created_at).toLocaleDateString('en-GB', { dateStyle: 'medium' })
        : '—',
      expiry: d.expiry_date || d.expires_at
        ? new Date(d.expiry_date || d.expires_at).toLocaleDateString('en-GB', { dateStyle: 'medium' })
        : 'N/A',
      status: rawStatus,
      reviewer: d.reviewer_name || d.reviewed_by || 'Unassigned'
    };
  });

  const docCounts = {
    total: documents.length,
    verified: documents.filter(d => d.status === 'APPROVED' || d.status === 'VERIFIED').length,
    underReview: documents.filter(d => d.status === 'UNDER REVIEW').length,
    expired: documents.filter(d => d.status === 'EXPIRED').length,
    rejected: documents.filter(d => d.status === 'REJECTED').length,
    missing: documents.filter(d => d.status === 'MISSING').length
  };

  const metrics = [
    { label: 'TOTAL DOCUMENTS', value: String(docCounts.total), icon: '📋', color: 'text-gray-900' },
    { label: 'VERIFIED', value: String(docCounts.verified), icon: '✓', color: 'text-green-600' },
    { label: 'UNDER REVIEW', value: String(docCounts.underReview), icon: '⏳', color: 'text-blue-600' },
    { label: 'MISSING', value: String(docCounts.missing), icon: '⚠️', color: 'text-orange-600' },
    { label: 'EXPIRED', value: String(docCounts.expired), icon: '❌', color: 'text-red-600' },
    { label: 'REJECTED', value: String(docCounts.rejected), icon: '⛔', color: 'text-red-700' }
  ];

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
      APPROVED: { bg: 'bg-green-100', text: 'text-green-700', dot: '● ' },
      'UNDER REVIEW': { bg: 'bg-blue-100', text: 'text-blue-700', dot: '● ' },
      EXPIRED: { bg: 'bg-red-100', text: 'text-red-700', dot: '● ' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-700', dot: '● ' },
      MISSING: { bg: 'bg-gray-100', text: 'text-gray-600', dot: '○ ' }
    };
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: '● ' };
    return <Badge className={`${config.bg} ${config.text} border-0`}>{config.dot}{status}</Badge>;
  };

  // Handler: Approve document
  const handleApproveDocument = async () => {
    if (!selectedDocumentForApproval) return;
    try {
      const response = await apiService.request(`/admin/provider-documents/${selectedDocumentForApproval.id}/approve`, 'POST', {
        expiryDate: approvalFormData.expiryDate,
        verificationLevel: approvalFormData.verificationLevel,
        internalNotes: approvalFormData.internalNotes,
        approvedBy: localStorage.getItem('adminId'),
        approvedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Document approved');
        setShowApproveModal(false);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Reject document
  const handleRejectDocument = async () => {
    if (!selectedDocumentForReject) return;
    try {
      const response = await apiService.request(`/admin/provider-documents/${selectedDocumentForReject.id}/reject`, 'POST', {
        reason: rejectFormData.primaryReason,
        explanation: rejectFormData.detailedExplanation,
        notifyEmail: rejectFormData.notifyEmail,
        rejectedBy: localStorage.getItem('adminId'),
        rejectedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Document rejected');
        setShowRejectModal(false);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Request document update
  const handleRequestDocumentUpdate = async () => {
    if (!selectedDocumentForRequest) return;
    try {
      const response = await apiService.request(`/admin/provider-documents/${selectedDocumentForRequest.id}/request-update`, 'POST', {
        documentType: requestFormData.documentType,
        message: requestFormData.message,
        deadline: requestFormData.deadline,
        requestedBy: localStorage.getItem('adminId'),
        requestedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Update request sent');
        setShowRequestModal(false);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Request resubmission
  // (audit 2026-06-11) — was reading selectedDocumentForReject instead of
  // selectedDocumentForResubmission, so the resubmit modal silently no-op'd.
  const handleRequestResubmission = async () => {
    const target = selectedDocumentForResubmission || selectedDocumentForReject;
    if (!target) return;
    try {
      const response = await apiService.request(`/admin/provider-documents/${(target as any).id}/request-resubmission`, 'POST', {
        issue: resubmissionFormData.issueDescription,
        deadline: resubmissionFormData.deadline,
        priority: resubmissionFormData.priority,
        requestedBy: localStorage.getItem('adminId'),
        requestedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Resubmission requested');
        setShowResubmissionModal(false);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export documents
  const handleExportDocuments = async () => {
    try {
      const response = await apiService.request('/admin/provider-documents/export', 'GET', {
        status: statusFilter
      });
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `documents-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Documents exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export audit report
  const handleExportAuditReport = async () => {
    try {
      const response = await apiService.request('/admin/provider-documents/audit-report', 'GET');
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Audit report exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Provider Documents - VireWire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="mb-2 text-sm text-gray-600">
                <span>COMPLIANCE</span>
                <span className="mx-2">{'>'}</span>
                <span className="text-gray-900 font-semibold">PROVIDER DOCUMENTS</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-1">Provider Documents</h1>
                  <p className="text-gray-600">All provider compliance document submissions</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res: any = await apiService.request('/admin/provider-documents/export', 'GET');
                      const data = res?.data || res;
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `provider-documents-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e: any) { alert('Export failed: ' + (e?.message || 'Network error')); }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="max-w-7xl mx-auto px-8 py-8">
            <div className="grid grid-cols-6 gap-4 mb-8">
              {metrics.map((metric, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-6">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{metric.label}</p>
                    <p className={`text-2xl font-bold ${metric.color}`}>{metric.value}</p>
                    {metric.change && <p className="text-xs text-gray-600 mt-1">{metric.change}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Filters */}
            <div className="mb-6 flex items-center gap-4">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">FILTERS</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 bg-white text-sm font-bold text-gray-700"
              >
                <option value="all">STATUS: All</option>
                <option value="pending">STATUS: Pending</option>
                <option value="approved">STATUS: Approved</option>
                <option value="rejected">STATUS: Rejected</option>
                <option value="expired">STATUS: Expired</option>
              </select>
            </div>

            {/* Documents Table */}
            <Card className="mb-8">
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Document Name</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Category</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Provider</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Uploaded</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Expiry Date</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Reviewer</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.length === 0 && (
                        <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">No documents found.</td></tr>
                      )}
                      {documents.map((doc) => (
                        <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 font-bold text-gray-900">{doc.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-700">{doc.category}</p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold text-xs">{doc.avatar}</div>
                              <p className="text-sm text-gray-900">{doc.provider}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{doc.uploaded}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{doc.expiry}</td>
                          <td className="px-6 py-4">{getStatusBadge(doc.status)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{doc.reviewer}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => {
                                  setSelectedDocumentForRequest(doc);
                                  setShowRequestModal(true);
                                }}
                                className="px-3 py-1 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                              >
                                Request Update
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDocumentForApproval(doc);
                                  setShowApproveModal(true);
                                }}
                                className="px-3 py-1 text-xs font-semibold text-white bg-teal-600 rounded hover:bg-teal-700 transition-colors"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDocumentForReject(doc);
                                  setShowRejectModal(true);
                                }}
                                className="px-3 py-1 text-xs font-semibold text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedDocumentForResubmission(doc);
                                  setShowResubmissionModal(true);
                                }}
                                className="px-3 py-1 text-xs font-semibold text-white bg-amber-600 rounded hover:bg-amber-700 transition-colors"
                              >
                                Resubmit
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600">Showing {documents.length} submissions</p>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100">Previous</button>
                    <button className="w-8 h-8 bg-teal-600 text-white text-sm font-bold rounded">1</button>
                    <button className="w-8 h-8 border border-gray-300 text-sm font-bold rounded hover:bg-gray-100">2</button>
                    <button className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100">Next</button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Health Score */}
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2">
                <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
                  <CardContent className="pt-6">
                    <h3 className="text-2xl font-bold mb-3">Compliance Health Score</h3>
                    <p className="text-sm text-green-100 mb-6">Your overall network compliance is at {docCounts.total > 0 ? Math.round((docCounts.verified / docCounts.total) * 100) : 0}%. We've identified {docCounts.missing} high-priority documents requiring immediate attention before EOD.</p>
                    <button
                      onClick={async () => {
                        try {
                          const res: any = await apiService.request('/admin/provider-documents/audit-report', 'GET');
                          const data = res?.data || res;
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `provider-document-audit-${new Date().toISOString().split('T')[0]}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (e: any) {
                          alert('Audit report failed: ' + (e?.message || 'Network error'));
                        }
                      }}
                      className="px-6 py-2 bg-white text-green-600 font-bold rounded-lg hover:bg-green-50"
                    >
                      Run Full Audit Report
                    </button>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">PENDING REQUESTS</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Expiring Lic. Alerts sent</span>
                      <span className="font-bold text-gray-900">{fetchedData.filter((d: any) => d.status === 'EXPIRED' || d.expiry_date && new Date(d.expiry_date) < new Date()).length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Manual overrides req.</span>
                      <span className="font-bold text-gray-900">{String(docCounts.rejected).padStart(2, '0')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Request Document Update Modal */}
        {showRequestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">COMPLIANCE ACTION</h2>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <h3 className="text-xl font-bold mb-2">Request Document Update</h3>
              <p className="text-sm text-gray-600 mb-6">
                Send a secure request to the provider to refresh their compliance documentation.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await handleRequestDocumentUpdate();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    DOCUMENT TYPE
                  </label>
                  <select
                    value={requestFormData.documentType}
                    onChange={(e) =>
                      setRequestFormData({ ...requestFormData, documentType: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>Tax Identification Certificate</option>
                    <option>Professional License</option>
                    <option>Insurance Certificate</option>
                    <option>Business Registration</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest">
                      REQUEST MESSAGE
                    </label>
                    <button
                      type="button"
                      className="text-xs font-bold text-teal-600 hover:text-teal-700"
                    >
                      USE TEMPLATE
                    </button>
                  </div>
                  <textarea
                    value={requestFormData.message}
                    onChange={(e) =>
                      setRequestFormData({ ...requestFormData, message: e.target.value })
                    }
                    placeholder="Enter instructions for the provider regarding the required updates..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    COMPLIANCE DEADLINE
                  </label>
                  <input
                    type="date"
                    value={requestFormData.deadline}
                    onChange={(e) =>
                      setRequestFormData({ ...requestFormData, deadline: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Grace period required per SLA standards
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
                  >
                    Send Request →
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Approve Document Modal */}
        {showApproveModal && selectedDocumentForApproval && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">COMPLIANCE VERIFICATION</h2>
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <h3 className="text-xl font-bold mb-4">Approve Document</h3>

              {/* Document Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="bg-teal-100 rounded-lg p-2">
                    <span className="text-lg">📄</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {selectedDocumentForApproval.name || 'Professional Indemnity Insurance'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Provider: {selectedDocumentForApproval.provider || 'Alastair Vance'}
                    </p>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600 ml-auto">
                    ↗
                  </button>
                </div>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await handleApproveDocument();
                }}
                className="space-y-4"
              >
                {/* Expiry Date Verification */}
                <div>
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    EXPIRY DATE VERIFICATION
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 font-semibold">
                      {approvalFormData.expiryDate}
                    </span>
                    <span className="text-xs text-green-600 font-semibold">
                      ● Valid for 364 more days
                    </span>
                  </div>
                </div>

                {/* Verification Level */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    VERIFICATION LEVEL
                  </label>
                  <select
                    value={approvalFormData.verificationLevel}
                    onChange={(e) =>
                      setApprovalFormData({
                        ...approvalFormData,
                        verificationLevel: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>Standard Review</option>
                    <option>Enhanced Review</option>
                    <option>Express Review</option>
                  </select>
                </div>

                {/* Internal Notes */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    INTERNAL NOTES
                  </label>
                  <textarea
                    value={approvalFormData.internalNotes}
                    onChange={(e) =>
                      setApprovalFormData({
                        ...approvalFormData,
                        internalNotes: e.target.value,
                      })
                    }
                    placeholder="Add administrative details or compliance observations..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  />
                </div>

                {/* Verification Checklist */}
                <div>
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">
                    VERIFICATION CHECKLIST
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={approvalFormData.entityMatch}
                        onChange={(e) =>
                          setApprovalFormData({
                            ...approvalFormData,
                            entityMatch: e.target.checked,
                          })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-teal-600"
                      />
                      <span className="text-sm text-gray-700">Entity Match</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={approvalFormData.validStamp}
                        onChange={(e) =>
                          setApprovalFormData({
                            ...approvalFormData,
                            validStamp: e.target.checked,
                          })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-teal-600"
                      />
                      <span className="text-sm text-gray-700">Valid Stamp</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={approvalFormData.externalApiSync}
                        onChange={(e) =>
                          setApprovalFormData({
                            ...approvalFormData,
                            externalApiSync: e.target.checked,
                          })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-teal-600"
                      />
                      <span className="text-sm text-gray-700">External API Sync</span>
                    </label>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowApproveModal(false);
                      setSelectedDocumentForReject(selectedDocumentForApproval);
                      setShowRejectModal(true);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reject Document
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowApproveModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
                  >
                    Approve Document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reject Document Modal */}
        {showRejectModal && selectedDocumentForReject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">Reject Document</h2>
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Specify the reason for rejection. This information will be logged in the provider's compliance history and shared with the administrator team.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await handleRejectDocument();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    PRIMARY REASON
                  </label>
                  <select
                    value={rejectFormData.primaryReason}
                    onChange={(e) =>
                      setRejectFormData({ ...rejectFormData, primaryReason: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>Burry or Unreadable Image</option>
                    <option>Incomplete Document</option>
                    <option>Expired Document</option>
                    <option>Invalid Signature</option>
                    <option>Missing Information</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest">
                      DETAILED EXPLANATION
                    </label>
                    <span className="text-xs text-gray-500">MIN 50 CHARACTERS</span>
                  </div>
                  <textarea
                    value={rejectFormData.detailedExplanation}
                    onChange={(e) =>
                      setRejectFormData({ ...rejectFormData, detailedExplanation: e.target.value })
                    }
                    placeholder="Describe the specific findings or corrective actions required by the provider..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                    minLength={50}
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rejectFormData.notifyEmail}
                      onChange={(e) =>
                        setRejectFormData({ ...rejectFormData, notifyEmail: e.target.checked })
                      }
                      className="w-5 h-5 rounded border-gray-300 text-blue-600"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-700">Notify Provider via Email</span>
                      <p className="text-xs text-gray-500">Send an automated rejection notification</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rejectFormData.restrictAccess}
                      onChange={(e) =>
                        setRejectFormData({ ...rejectFormData, restrictAccess: e.target.checked })
                      }
                      className="w-5 h-5 rounded border-gray-300 text-blue-600"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-700">Restrict Provider Access</span>
                      <p className="text-xs text-gray-500">Prevent portal access until document is re-verified</p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <span>⚠️</span>
                    Reject & Notify
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Request Resubmission Modal */}
        {showResubmissionModal && selectedDocumentForResubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">ADMINISTRATIVE ACTION</h2>
                <button
                  onClick={() => setShowResubmissionModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <h3 className="text-xl font-bold mb-2">Request Resubmission</h3>
              <p className="text-sm text-gray-600 mb-6">
                Notify the provider about required document corrections.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await handleRequestResubmission();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    ISSUE DESCRIPTION
                  </label>
                  <textarea
                    value={resubmissionFormData.issueDescription}
                    onChange={(e) =>
                      setResubmissionFormData({ ...resubmissionFormData, issueDescription: e.target.value })
                    }
                    placeholder="Detail the specific corrections required..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[100px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      DEADLINE
                    </label>
                    <input
                      type="date"
                      value={resubmissionFormData.deadline}
                      onChange={(e) =>
                        setResubmissionFormData({ ...resubmissionFormData, deadline: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      PRIORITY
                    </label>
                    <select
                      value={resubmissionFormData.priority}
                      onChange={(e) =>
                        setResubmissionFormData({ ...resubmissionFormData, priority: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option>Low</option>
                      <option>Standard</option>
                      <option>High</option>
                      <option>Urgent</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resubmissionFormData.restrictPortalAccess}
                    onChange={(e) =>
                      setResubmissionFormData({ ...resubmissionFormData, restrictPortalAccess: e.target.checked })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-blue-600"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Restrict Portal Access</span>
                    <p className="text-xs text-gray-500">Limit provider access until corrections are made</p>
                  </div>
                </label>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowResubmissionModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-semibold"
                  >
                    SEND REQUEST
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminProviderDocumentsPage;
